import type {AnyObject, NonNullishProperties, StringifiedNumber, Truthy} from './types'

/** Returns typed array of object's keys */
export const typedKeys = <T extends Record<string, any>>(data: T): Array<keyof T> =>
    Object.keys.call<typeof data, Array<typeof data>, Array<keyof T>>(data, data)

/** Typed version of Object.entries */
export const typedEntries = <T extends Record<string, any>>(data: T): Array<[keyof T, T[keyof T]]> =>
    Object.entries(data) as [keyof T, T[keyof T]][]

/** Asserts that argument is defined */
export function assertDefined<D>(data: D, errorMessage?: string): asserts data is Exclude<D, undefined | null> {
    if (typeof data === 'undefined' || data === null) {
        throw new Error(errorMessage ?? 'The assertDefined check is not passed. Argument has type undefined')
    }
}

export function assertStringArray(data: unknown): asserts data is string[] {
    if (!isStringArray(data)) {
        throw new Error('The assertStringArray check is not passed.')
    }
}

export function isStringArray(data: unknown): data is string[] {
    return Array.isArray(data) && data.every(i => typeof i === 'string')
}

/** Asserts that argument is true */
export function assertTrue(value: boolean, errorMessage?: string): asserts value is true {
    if (value !== true) {
        throw new Error(errorMessage ?? 'The assertTrue check is not passed')
    }
}

/** Asserts that argument properties is defined */
export function assertDefinedProperties<D extends Record<string, unknown>, K extends keyof D>(
    data?: D,
    pick?: K | K[],
    errorMessage?: string,
): asserts data is NonNullishProperties<D> {
    assertDefined(data)

    if (pick) {
        const pickArr = Array.isArray(pick) ? pick : [pick]
        pickArr.forEach(prop => {
            if (typeof data[prop] === 'undefined' || data[prop] === null) {
                throw new Error(
                    errorMessage ??
                    `The assertDefinedProp check is not passed. Argument ${String(prop)} has type undefined`,
                )
            }
        })
    } else {
        if (
            Object.values(data).some(
                value => typeof value === 'undefined' || value === null,
            )
        ) {
            throw new Error(
                errorMessage ??
            'The assertDefinedProperties check is not passed. One of argument\'s properties has type undefined',
            )
        }
    }
}

export function every<T>(array: unknown[], checker: (prop: unknown) => prop is T): array is T[] {
    return array.every(checker)
}

export function isDefined<T>(value: T): value is Exclude<T, undefined> {
    return value !== undefined
}

/** Generalises any type to Object */
export const isObject = (data: unknown): data is AnyObject => data instanceof Object
export function assertIsObject(
    data: unknown,
    msg = 'Not an object',
): asserts data is AnyObject {
    if (!isObject(data)) {
        throw new Error(msg)
    }
}

export function isStringifiedNumber(value: unknown): value is StringifiedNumber {
    return typeof value === 'string' && !isNaN(Number(value))
}

export const truthy = <T>(arr: T[]): Truthy<T>[] => arr.filter(Boolean) as Truthy<T>[]

export const includes = <T extends string>(arr: T[] | readonly T[], str: string): str is T => arr.some(i => i === str)

export const filter = <T, R extends T>(arr: T[] | readonly T[], callback: (arg: T) => arg is R): R[] => arr.filter(callback) as R[]

export const isPromise = (data: unknown): data is Promise<unknown> => data instanceof Promise


/** Transforms type from specific literals array to unspecified string array
 * e.g. readonly ['first', 'second'] --> readonly string[] */
export const array = <T extends readonly string[]>(arr: T): readonly string[] => {
    return arr
}

export function isInArray<T, Element extends T>(
    element: T,
    arr: readonly Element[],
): element is Element {
    const arrT: readonly T[] = arr
    return arrT.includes(element)
}

/**
 * Returns new object identical to provided but only without keys with undefined value
 * @param value Any object
 */
export function withoutUndefinedKeys<T extends {[key: string]: unknown}>(value: T): T {
    return typedKeys(value).reduce<T>((newObj, key) => {
        if (value[key] !== undefined) {
            newObj[key] = value[key]
        }
        return newObj
    }, {} as T)
}

export function hasOwnKey<T extends object, K extends PropertyKey>(obj: T, key: K): key is K & keyof T {
    return key in obj
}

/**
 * Removes all object keys using "delete". Basically, object cleanup with reference preservation
 * Warning: not type safe!
 * @param value Any object
 */
export function clearObject<T extends object>(value: T): T {
    if (isObject(value)) {
        Object.getOwnPropertyNames(value).forEach(key => delete value[key])
    }
    return value
}

export function assertNotNull<T>(value: T | null): asserts value is Exclude<T, null> {
    if (value === null) {
        throw Error('assertNotNull failed')
    }
}

/** Usefull for switch-case default branch */
export function assertNever(x: never, message?: string): never {
    throw new Error(message || `Unexpected value`, x)
}

export function isStringInEnum<T extends { [key: string]: string }>(enumType: T, value: string | undefined): value is T[keyof T] {
    return value !== undefined && Object.values(enumType).includes(value as T[keyof T])
}
